import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import EventCard from "@/components/EventCard";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate, Link } from "react-router-dom";
import { Ticket, Heart, X, Loader2, UserCircle2, Camera, Gift, Copy, Award, ShoppingBag, MapPin as MapPinIcon, PackageCheck } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";

const INTERESTS = ["Events", "House Parties", "Trips", "After Parties", "Single", "In Relationship", "College Student"];

function CompletionBar({ percent, label = "Profile completion" }) {
  return (
    <div data-testid="profile-completion-bar">
      <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest mb-1">
        <span className="text-ink/70">{label}</span>
        <span className="text-ink font-bold" data-testid="profile-completion-percent">{percent}%</span>
      </div>
      <div className="h-2 rounded-full bg-ink/10 overflow-hidden">
        <div className="h-full bg-ink transition-all duration-500" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

function ProfileCompletionForm({ user, refresh }) {
  const [form, setForm] = useState({
    name: user.name || "",
    phone: user.phone || "",
    instagram: user.instagram || "",
    date_of_birth: user.date_of_birth || "",
    gender: user.gender || "",
    interests: user.interests || [],
    profile_picture: user.profile_picture || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const toggleInterest = (i) =>
    setForm((f) => ({ ...f, interests: f.interests.includes(i) ? f.interests.filter((x) => x !== i) : [...f.interests, i] }));

  const uploadPic = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Max 5MB");
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/uploads?kind=profile-pic", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/files/${data.path}`;
      setForm((f) => ({ ...f, profile_picture: url }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/users/me", form);
      toast.success("Profile updated");
      await refresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="glass rounded-3xl p-6 space-y-4 mb-8" data-testid="profile-form">
      <div className="flex items-start gap-4">
        <label className="cursor-pointer shrink-0" data-testid="profile-pic-label">
          <div className="w-20 h-20 rounded-full bg-white/5 border-2 border-dashed border-lime/40 overflow-hidden flex items-center justify-center relative">
            {form.profile_picture ? (
              <img src={form.profile_picture} alt="" className="w-full h-full object-cover" />
            ) : (
              <Camera className="w-6 h-6 text-lime/60" />
            )}
            {uploading && <div className="absolute inset-0 bg-black/50 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-white" /></div>}
          </div>
          <input type="file" accept="image/*" hidden onChange={(e) => uploadPic(e.target.files?.[0])} data-testid="profile-pic-input" />
        </label>
        <div className="flex-1">
          <div className="font-mono text-[10px] uppercase tracking-widest text-lime">Complete your vibe</div>
          <div className="font-display text-2xl uppercase mt-1 leading-tight">Tell us about you</div>
          <p className="text-xs text-muted font-mono mt-1">A complete profile unlocks VIP eligibility & better recommendations.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <input required data-testid="profile-name-input" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-dark" />
        <input data-testid="profile-phone-input" placeholder="Mobile (+91...)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-dark" inputMode="tel" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <input data-testid="profile-instagram-input" placeholder="Instagram (@handle)" value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} className="input-dark" />
        <input type="date" data-testid="profile-dob-input" placeholder="Date of birth" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} className="input-dark" />
      </div>

      <div>
        <label className="text-[10px] font-mono uppercase tracking-widest text-muted">Gender (required for VIP eligibility)</label>
        <div className="mt-1 inline-flex gap-1 p-1 rounded-full bg-surface" data-testid="profile-gender">
          {[["male", "Male"], ["female", "Female"], ["other", "Other"]].map(([k, l]) => (
            <button type="button" key={k} data-testid={`gender-${k}`} onClick={() => setForm({ ...form, gender: k })}
              className={`px-4 py-1.5 text-xs font-mono uppercase tracking-widest rounded-full ${form.gender === k ? "bg-lime text-ink" : "text-muted"}`}>{l}</button>
          ))}
        </div>
      </div>

      <div>
        <label className="text-[10px] font-mono uppercase tracking-widest text-muted mb-2 block">Interests — tap to select</label>
        <div className="flex flex-wrap gap-2" data-testid="profile-interests">
          {INTERESTS.map((i) => {
            const on = form.interests.includes(i);
            return (
              <button type="button" key={i} data-testid={`interest-${i.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => toggleInterest(i)}
                className={`px-3 py-1.5 rounded-2xl border text-xs font-mono uppercase tracking-widest transition-all ${on ? "bg-pink text-white border-pink" : "bg-white/5 border-white/10 text-muted hover:text-white"}`}>
                {i}
              </button>
            );
          })}
        </div>
      </div>

      <button disabled={saving} data-testid="profile-save" className="btn-primary w-full justify-center">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCircle2 className="w-4 h-4" />}
        {saving ? "Saving..." : "Save Profile"}
      </button>
    </form>
  );
}


const STATUS_META = {
  pending:   { label: "Pending payment", color: "text-orangex", bg: "bg-orangex/15 border-orangex/40" },
  paid:      { label: "Paid · Processing", color: "text-lime",    bg: "bg-lime/15 border-lime/40" },
  packed:    { label: "Packed", color: "text-skyx", bg: "bg-skyx/15 border-skyx/40" },
  shipped:   { label: "Shipped", color: "text-magenta", bg: "bg-magenta/15 border-magenta/40" },
  delivered: { label: "Delivered", color: "text-lime", bg: "bg-lime/15 border-lime/40" },
};
function StoreOrdersSection() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/store/orders/me").then(({ data }) => setOrders(data || [])).catch(() => setOrders([])).finally(() => setLoading(false));
  }, []);
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10" data-testid="profile-my-orders">
      <div className="flex items-end justify-between gap-3 mb-4">
        <h2 className="font-display uppercase text-3xl inline-flex items-center gap-2"><ShoppingBag className="w-6 h-6 text-lime" /> My Orders</h2>
        <Link to="/store" className="text-[10px] font-mono uppercase tracking-widest text-lime">Shop more →</Link>
      </div>
      {loading ? (
        <div className="card-dark p-6 font-mono text-sm text-muted">Loading…</div>
      ) : orders.length === 0 ? (
        <div className="card-dark p-6 font-mono text-sm text-muted flex items-center justify-between gap-3">
          <span>No orders yet. Wear the vibe — visit the Store.</span>
          <Link to="/store" className="btn-lime !py-2 !px-4 !text-xs">Shop Now</Link>
        </div>
      ) : (
        <div className="grid gap-3">
          {orders.map((o) => {
            const meta = STATUS_META[o.fulfillment && o.status === "paid" ? o.fulfillment : o.status] || STATUS_META.paid;
            return (
              <div key={o.id} data-testid={`order-${o.id}`} className="rounded-3xl border border-white/10 bg-white/[0.04] overflow-hidden grid sm:grid-cols-[112px,1fr,auto] gap-3 items-center p-3 pr-4">
                <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl overflow-hidden bg-black/30 shrink-0">
                  {o.product_image && <img src={o.product_image} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="min-w-0">
                  <div className="font-heading font-bold text-white text-sm leading-tight line-clamp-2">{o.product_title}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted mt-1">
                    {o.size ? `Size ${o.size} · ` : ""}Qty {o.quantity} · Placed {new Date(o.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                  </div>
                  <div className="text-[11px] text-muted mt-1 inline-flex items-center gap-1"><MapPinIcon className="w-3 h-3 text-magenta" /> {o.buyer?.city}, {o.buyer?.state} · {o.buyer?.pincode}</div>
                  <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border ${meta.bg} ${meta.color} text-[9px] font-mono uppercase tracking-widest font-bold`}><PackageCheck className="w-3 h-3" /> {meta.label}</span>
                    {o.coupon_code && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-magenta/15 border border-magenta/40 text-magenta text-[9px] font-mono uppercase tracking-widest">{o.coupon_code} · − ₹{o.coupon_discount}</span>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-fancy text-2xl text-lime">₹{Number(o.amount).toLocaleString("en-IN")}</div>
                  <div className="text-[9px] font-mono uppercase tracking-widest text-muted">incl. shipping</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default function Profile() {
  const { user, loading, refresh } = useAuth();
  const [regs, setRegs] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [favs, setFavs] = useState([]);
  const [qr, setQr] = useState(null);
  const [referral, setReferral] = useState(null);
  const [showEdit, setShowEdit] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/registrations/me").then(({ data }) => setRegs(data)).catch(() => {});
    api.get("/tickets/me").then(({ data }) => setTickets(data)).catch(() => {});
    api.get("/favorites/me").then(({ data }) => setFavs(data.events || [])).catch(() => {});
    api.get("/referrals/me").then(({ data }) => setReferral(data)).catch(() => {});
  }, [user]);

  if (loading) return <Layout><div className="p-10 font-display text-3xl uppercase">Loading...</div></Layout>;
  if (!user) return <Navigate to="/login" replace />;

  const completion = user.profile_completion ?? 0;

  return (
    <Layout>
      <section className="section-lime border-b border-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 flex flex-wrap items-center gap-6">
          {user.profile_picture ? (
            <img src={user.profile_picture} alt={user.name} className="w-24 h-24 rounded-full object-cover border-4 border-ink" />
          ) : (
            <div className="w-24 h-24 rounded-full bg-pink text-white flex items-center justify-center text-4xl font-display border-4 border-ink">{user.name?.[0]}</div>
          )}
          <div className="flex-1 min-w-0">
            <div className="chip bg-ink text-lime">Your Profile</div>
            <h1 className="font-fancy uppercase text-4xl md:text-6xl leading-[0.95] break-words" data-testid="profile-name">{user.name}</h1>
            <div className="mt-2 font-mono text-sm text-ink/70 break-all">{user.email} {user.phone && <span>• {user.phone}</span>} • <span className="bg-ink text-lime px-2 py-0.5 rounded-full uppercase">{user.role}</span></div>
            <div className="mt-4 max-w-md">
              <CompletionBar percent={completion} />
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        {(completion < 100 || showEdit) ? (
          <div className="mb-6">
            <div className="flex items-center justify-between gap-3 mb-2">
              <div className="font-display text-3xl">{completion < 100 ? "Complete profile" : "Edit profile"}</div>
              {completion >= 100 && (
                <button onClick={() => setShowEdit(false)} data-testid="profile-edit-close" className="btn-ghost !py-2 !px-3 !text-xs">Done</button>
              )}
            </div>
            <ProfileCompletionForm user={user} refresh={refresh} />
          </div>
        ) : (
          <button onClick={() => setShowEdit(true)} data-testid="profile-edit-btn" className="btn-lime mb-6">Edit profile</button>
        )}

        {/* Phase 4: Loyalty + Referrals */}
        <div className="grid md:grid-cols-2 gap-4 mb-8">
          <div className="glass rounded-3xl p-5" data-testid="loyalty-card">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-lime"><Award className="w-3.5 h-3.5" /> Loyalty · {user.loyalty_tier || "Explorer"}</div>
            <div className="font-display uppercase text-5xl grad-text-hot mt-2" data-testid="loyalty-points">{user.loyalty_points ?? 0} pts</div>
            <div className="text-xs text-muted mt-2">100 points = ₹10 off at checkout. Earn 50/ticket · 100/profile complete · 100/referral converted.</div>
          </div>
          <div className="glass rounded-3xl p-5" data-testid="referral-card">
            <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-pink"><Gift className="w-3.5 h-3.5" /> Refer & Earn</div>
            <div className="font-display uppercase text-2xl mt-2">Your code: <span className="text-lime" data-testid="referral-code">{referral?.code || user.referral_code || "—"}</span></div>
            <div className="mt-2 flex items-center gap-2">
              <input readOnly value={referral?.link || `https://eventom.online/?ref=${user.referral_code || ""}`} className="input-dark !py-2 !text-xs flex-1 font-mono" data-testid="referral-link" />
              <button onClick={() => { navigator.clipboard.writeText(referral?.link || ""); toast.success("Link copied"); }} data-testid="referral-copy" className="btn-ghost !py-2 !px-3 !text-xs"><Copy className="w-3.5 h-3.5" /> Copy</button>
            </div>
            <div className="mt-3 text-xs text-muted">Converted: <span className="text-lime">{referral?.converted ?? 0}</span> · Pending: <span className="text-orangex">{referral?.pending ?? 0}</span></div>
            <Link to="/leaderboard" data-testid="referral-leaderboard-link" className="arrow-link mt-3 inline-flex !text-[10px]">See the squad leaderboard</Link>
          </div>
        </div>

        <h2 className="font-display uppercase text-3xl mb-4">My Tickets</h2>
        {tickets.length === 0 ? (
          <div className="card-dark p-6 font-mono text-sm text-muted">No tickets yet. <Link to="/" className="text-lime underline">Discover events →</Link></div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {tickets.map((t) => (
              <button key={t.id} onClick={() => setQr(t)} className="card-dark p-4 text-left card-lift" data-testid={`ticket-${t.id}`}>
                <div className="flex items-start gap-3">
                  <div className="icon-circle !w-12 !h-12 bg-pink text-white shrink-0"><Ticket className="w-5 h-5" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display uppercase text-lg leading-tight break-words">{t.event_title}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted mt-1">{t.tier} • Qty {t.quantity} • ₹{t.amount}</div>
                    <div className="text-xs font-mono mt-2 text-lime">Code: {t.ticket_code}</div>
                    {t.entry_status === "entered" && <div className="text-[10px] font-mono uppercase text-orangex mt-1">✓ Entered {new Date(t.scanned_at).toLocaleDateString()}</div>}
                  </div>
                </div>
                <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-lime">Tap to view QR</div>
              </button>
            ))}
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <h2 className="font-display uppercase text-3xl mb-4"><Heart className="inline w-6 h-6 text-pink mb-1" fill="currentColor" /> Saved Events</h2>
        {favs.length === 0 ? (
          <div className="card-dark p-6 font-mono text-sm text-muted">No saved events. Tap the heart on any event card.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
            {favs.map((e, i) => <EventCard key={e.id} event={e} index={i} favorited />)}
          </div>
        )}
      </section>

      <StoreOrdersSection />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <h2 className="font-display uppercase text-3xl mb-4">My Registrations</h2>
        {regs.length === 0 ? (
          <div className="card-dark p-6 font-mono text-sm text-muted">No registrations yet.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {regs.map((r) => (
              <div key={r.id} className="card-dark p-4" data-testid={`reg-${r.id}`}>
                <div className="font-display uppercase text-lg leading-tight break-words">{r.event_title}</div>
                <div className="text-[10px] font-mono uppercase text-muted mt-1">Status: {r.status}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {qr && (
        <div className="fixed inset-0 bg-ink/90 backdrop-blur-md z-50 flex items-center justify-center p-4" onClick={() => setQr(null)}>
          <div className="polaroid !p-4 !pb-14 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-2">
              <div className="min-w-0 pr-2">
                <div className="font-mono text-[9px] tracking-widest uppercase text-ink/50">Admit One Eventom</div>
                <div className="font-display uppercase text-lg text-ink leading-tight break-words">{qr.event_title}</div>
              </div>
              <button onClick={() => setQr(null)} className="text-ink/60"><X className="w-5 h-5" /></button>
            </div>
            <div className="bg-white p-4 flex items-center justify-center">
              <QRCodeSVG value={JSON.stringify({ code: qr.ticket_code, session: qr.session_id })} size={220} bgColor="#ffffff" fgColor="#0A0A0A" level="H" />
            </div>
            <div className="mt-3 font-mono text-xs text-ink">
              <div className="flex justify-between"><span>Code</span><span className="font-bold">{qr.ticket_code}</span></div>
              <div className="flex justify-between"><span>Tier</span><span>{qr.tier}</span></div>
              <div className="flex justify-between"><span>Qty</span><span>{qr.quantity}</span></div>
              <div className="flex justify-between"><span>Paid</span><span>₹{qr.amount}</span></div>
            </div>
            <div className="mt-3 font-script text-pink text-xl absolute bottom-2 left-4">admit one eventom</div>
          </div>
        </div>
      )}
    </Layout>
  );
}
